import { TrackSegmentBehavior } from '@com/components/timeline-panel/utils/behavior'
import {
  getIvRelativedSegments,
  getRelativedIv,
  getSceneSegments,
  isAssetSegment,
  isSegmentAudio,
  isSegmentFreeze,
  isSegmentIv,
  isSegmentLoop,
  isSegmentVideo,
} from '.'
import { IvComponentSegment, IvNodeUuid, IvSegment, IvTrack } from '../types'
import { getIvAsset } from '../timeline'
import { TrackSegmentTheme } from '@com/components/timeline-panel/types'
import { IvLoopSubType, IvSegmentType, IvTrackType } from '../enums'
import { removeIvSegment, tryInsertIfConflictOrNot } from './track-action'
import { findSceneSegment, findVideoSegmentFromTracks } from './scene'
import { durationToFrames } from '@com/components/timeline-panel/utils/frame'
import { IV_DURATION, IV_FREEZE_DURATION } from './resolve-segment'
import { resolveSegmentFramesIfGap } from '@com/components/timeline-panel/utils/segment'
import { cloneDeep, merge } from 'lodash-es'
import { remove } from '@com/components/timeline-panel/utils/track-action'
import { getIvSceneLinkNode } from '../../brain/brain'
import { generateTrackSegmentId } from '@com/components/timeline-panel/utils/generate-id'

const themeMap: Record<IvSegmentType, TrackSegmentTheme> = {
  [IvSegmentType.Audio]: 'audio',
  [IvSegmentType.Video]: 'video',
  [IvSegmentType.Hand]: 'hand',
  [IvSegmentType.Image]: 'image',
  [IvSegmentType.Text]: 'text',
  [IvSegmentType.Freeze]: 'iv',
  [IvSegmentType.Loop]: 'iv',
  [IvSegmentType.Hold]: 'iv',
}

export function patchSegment(segment: IvSegment, data: IvTrack[]): void {
  if (isAssetSegment(segment)) {
    segment.asset = getIvAsset(segment.id)

    // 空资源的视频，禁用
    // segment.disabled = !segment.asset
  }

  const disabledAllBehaviorWhenAssetIsNull = isAssetSegment(segment)
    ? !segment.asset
    : false

  const enableBehavior = !disabledAllBehaviorWhenAssetIsNull

  const unsupportCut =
    // 互动定格，不支持裁剪
    isSegmentFreeze(segment) ||
    // 若为附属元件，不支持裁剪
    (!isSegmentIv(segment) && Boolean(getRelativedIv(segment, data)))

  // ”场景间“互动循环，仅支持“左侧”裁剪
  const isEndLoop = isSegmentLoop(segment) && segment.isEnd
  const supportCutLeft = !unsupportCut
  const supportCutRight = !unsupportCut && !isEndLoop

  segment.behavior = {
    // [TrackSegmentBehavior.Select]: enableBehavior,
    // 不支持拖拽：1. ”场景间“互动元件 2. 视频元件
    [TrackSegmentBehavior.Drag]:
      enableBehavior &&
      !(isSegmentVideo(segment) || (isSegmentIv(segment) && segment.isEnd)),

    [TrackSegmentBehavior.CutLeft]: enableBehavior && supportCutLeft,
    [TrackSegmentBehavior.CutRight]: enableBehavior && supportCutRight,
  }

  /**
   * 互动元件：场景内、场景间
   * 其他元件：附属元件、非附属元件
   */
  segment.theme = resolveSegmentTheme(segment, data)

  // 互动元件有范围的样式
  segment.styles = isSegmentIv(segment) ? ['area'] : []

  // 定格元件，称之为`gap segment`
  segment.is = isSegmentFreeze(segment) ? 'gap' : undefined
}

export function resolveSegmentTheme(
  segment: IvSegment,
  data: IvTrack[],
): TrackSegmentTheme {
  if (isSegmentIv(segment)) {
    return segment.isEnd ? 'iv' : 'iv-green'
  } else if (isSegmentAudio(segment)) {
    return 'audio'
  } else if (isSegmentVideo(segment)) {
    if (segment.isEnd) {
      return 'end'
    }
  }

  // 附属元件的
  const relativedIv = getRelativedIv(segment, data)
  if (relativedIv) {
    return relativedIv.isEnd ? 'iv-light' : 'iv-green-light'
  }

  return themeMap[segment.type]
}

/**
 * 删除场景间互动元件，并删除对应的附属元件
 * 1. 场景间互动循环、互动定格 切换变成 “自动跳转”
 */
export function removeSegmentIv(
  segment: IvComponentSegment,
  tracks: IvTrack[],
): void {
  const relativedSegments = getIvRelativedSegments(segment, tracks)

  relativedSegments.forEach((s) => {
    removeIvSegment(s, tracks, {
      removeTrackIfEmpty: true,
    })
  })

  removeIvSegment(segment, tracks, {
    removeTrackIfEmpty: true,
  })
}

/**
 * 获取指定进场时间，在互动轨道中，可放置的最大时长
 * @description 不支持 场景间互动元件的情况
 */
export function getIvTrackAvailableMaxFrames(options: {
  start: IvComponentSegment['start']
  sceneUuid: IvComponentSegment['sceneUuid']
  tracks: IvTrack[]
}): IvSegment['frames'] | -1 {
  const { start, sceneUuid, tracks } = options
  const videoSegment = findVideoSegmentFromTracks(sceneUuid, tracks)
  if (!videoSegment) return -1

  const targetIvTrack = tracks.find((t) => t.type === IvTrackType.Iv)

  if (!targetIvTrack) {
    return videoSegment.start + videoSegment.frames - start
  }

  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const sceneIvSegments = sceneSegments.filter(isSegmentIv)

  const behind = sceneIvSegments
    .sort((a, b) => a.start - b.start)
    .find((s) => s.start >= start)

  if (behind) {
    return behind.start - start
  }

  return videoSegment.start + videoSegment.frames - start
}

/**
 * 切换互动元件类型
 * 1. 附属元件关联调整
 */
export function updateSegmentIvType(
  segment: IvComponentSegment,
  targetType: IvComponentSegment['type'],
  tracks: IvTrack[],
): void {
  const sceneSegments = getSceneSegments(segment.sceneUuid, tracks)
  const videoSegment = findVideoSegmentFromTracks(segment.sceneUuid, tracks)
  // 若类型一致、目标元件不在剧本里面，则不处理
  if (
    !videoSegment ||
    segment.type === targetType ||
    !sceneSegments.some((s) => s.id === segment.id)
  ) {
    return
  }
  const sceneIvSegments = sceneSegments.filter(isSegmentIv)
  const sortedSceneIvSegments = sceneIvSegments.sort(
    (a, b) => a.start - b.start,
  )

  const last = sortedSceneIvSegments.findLast((s) => !s.isEnd)
  const behindLastStart = last ? last.start + last.frames : videoSegment.start

  const relativedSegments = getIvRelativedSegments(segment, tracks)
  if (targetType === IvSegmentType.Freeze) {
    segment.start = segment.isEnd
      ? videoSegment.start + videoSegment.frames - 1
      : segment.start
    segment.frames = durationToFrames(IV_FREEZE_DURATION)
    segment.type = targetType
    patchSegment(segment, tracks)
    relativedSegments.forEach((s) => {
      s.start = segment.start
      s.frames = resolveSegmentFramesIfGap(segment)
    })
  } else {
    const start = segment.isEnd ? behindLastStart : segment.start
    const maxFrames = getIvTrackAvailableMaxFrames({
      start: start,
      sceneUuid: segment.sceneUuid,
      tracks: tracks.map((t) => ({
        ...t,
        segments: [...(t.segments.filter((s) => s.id !== segment.id) as any)],
      })),
    })
    segment.frames = Math.min(maxFrames, durationToFrames(IV_DURATION))
    segment.type = targetType
    patchSegment(segment, tracks)
    if (segment.isEnd) {
      segment.start =
        videoSegment.start +
        videoSegment.frames -
        resolveSegmentFramesIfGap(segment)
    } else {
      // 不处理：保证原始位置
    }

    if (targetType === IvSegmentType.Loop && isSegmentLoop(segment)) {
      segment.loopSubType = IvLoopSubType.Single
    }
    relativedSegments.forEach((s) => {
      s.start = segment.start
      s.frames = resolveSegmentFramesIfGap(segment)
      // 如果发生冲突了
      tryInsertIfConflictOrNot(s, tracks)
    })
  }
}

/**
 * 根据视频元件更新指定剧本
 */
export function updateByVideoSegment(
  sceneUuid: IvSegment['sceneUuid'],
  tracks: IvTrack[],
): void {
  const target = findVideoSegmentFromTracks(sceneUuid, tracks)
  // 视频不存在就不处理了
  if (!target) return

  const sceneSegments = getSceneSegments(sceneUuid, tracks)
  const sceneIvComponentSegments = sceneSegments.filter(isSegmentIv)
  // 裁剪后，重新计算“场景”内所有元件所有的进场、时长信息
  //       视频时长范围变短，压缩时长优先级：优先确保场景间的展示，有冲突先压缩场景内的
  //       计算当前场景元件信息后，重新调用合并所有场景能力，计算出其他场景的相对位置
  const endIvSegment = sceneIvComponentSegments.find(
    (s) => s.sceneUuid === sceneUuid && s.isEnd,
  )

  const endIvRelativedSegments = endIvSegment
    ? getIvRelativedSegments(endIvSegment, tracks)
    : []

  if (endIvSegment) {
    // 场景间的互动循环，时长是有意义的
    if (endIvSegment.type === IvSegmentType.Loop) {
      endIvSegment.frames = Math.min(endIvSegment.frames, target.frames)
    }

    const endIvSegmentFrames = endIvSegment
      ? resolveSegmentFramesIfGap(endIvSegment)
      : 0
    endIvSegment.start = target.start + target.frames - endIvSegmentFrames

    endIvRelativedSegments.forEach((s) => {
      s.start = endIvSegment.start
      s.frames = resolveSegmentFramesIfGap(endIvSegment)
    })
  }

  /**
   * 场景内互动元件+附属元件+其他普通元件
   * 1. 去除场景间互动元件
   * 2. 去除场景间互动元件的附属元件
   */
  const excludeEndSegments = sceneSegments.filter(
    (s) =>
      !(
        isSegmentVideo(s) ||
        s.id === endIvSegment?.id ||
        endIvRelativedSegments.some((es) => es.id === s.id)
      ),
  )

  const _exec = (segments: IvSegment[]) => {
    segments.forEach((s) => {
      const _relativedSegments = isSegmentIv(s)
        ? getIvRelativedSegments(s, tracks)
        : []

      const targetStart = target.start
      const endIvSegmentFrames = endIvSegment
        ? resolveSegmentFramesIfGap(endIvSegment)
        : 0
      const targetFrames = target.frames - endIvSegmentFrames

      if (
        s.start + resolveSegmentFramesIfGap(s) <= targetStart || // 在左侧
        // 在右侧
        s.start >= targetStart + targetFrames
      ) {
        removeIvSegment(s, tracks)
        _relativedSegments.forEach((_rs) => {
          removeIvSegment(_rs, tracks)
        })
      } else {
        // 1. 按视频范围裁剪
        // 2. 结合场景间元件endIvSegment判断是否和附属元件冲突，是的话进一步裁剪
        const someTrackRelativedSegment = [
          ...endIvRelativedSegments,
          ...(endIvSegment ? [endIvSegment] : []),
        ].find((relativedSegment) => relativedSegment.trackId === s.trackId)

        /**
         * left start
         */
        const beforeStart = s.start
        s.start = Math.max(s.start, target.start)
        // + offsetStart -
        const offsetStart = beforeStart - s.start
        s.frames += offsetStart

        /**
         * right end
         */
        const minEndFrame = Math.min(
          // 自己
          s.start + s.frames,
          // 视频end（如果后面存在同轨道的场景间元件的附属元件，则用附属元件的进场做为end）
          someTrackRelativedSegment
            ? someTrackRelativedSegment.start
            : target.start + target.frames,
        )

        s.frames = minEndFrame - s.start

        _relativedSegments.forEach((_rs) => {
          _rs.start = s.start
          _rs.frames = resolveSegmentFramesIfGap(s)
        })
      }
    })
  }

  // 优先处理场景内的互动元件(附带处理‘附属元件’)
  const excludeEndIvSegments = sceneIvComponentSegments.filter((s) => !s.isEnd)
  _exec(excludeEndIvSegments)

  // 其他普通元件
  const otherSegments = excludeEndSegments.filter((s) => {
    return !isSegmentIv(s) && !getRelativedIv(s, tracks)
  })
  _exec(otherSegments)

  const emptyTracks = tracks.filter((t) => t.segments.length === 0)
  emptyTracks.forEach((t) => remove(t, tracks))
}

export function updateSegment<T extends IvSegment>(
  target: T,
  values: Partial<T>,
  tracks: IvTrack[],
): T | null {
  const segments = getSceneSegments(target.sceneUuid, tracks)
  const segment = segments.find((s): s is T => s.id === target.id)

  if (!segment) return null

  merge(segment, values)

  return segment
}

export interface CopySegmentOptions<
  T extends IvSegment,
  R extends keyof T = 'sceneUuid' | 'start',
> {
  target: T
  override: Pick<T, R>
}

export function copySegment<T extends IvSegment>(
  options: CopySegmentOptions<T>,
): T {
  const { target, override } = options

  const newSegment = merge(
    cloneDeep(target),
    { id: generateTrackSegmentId() as T['id'] },
    override,
  )

  return newSegment
}

// 根据节点uuid获取其对应的segment id
export function getSegmentIdByNodeUuid(
  sceneUuid: IvSegment['sceneUuid'],
  nodeUuid: IvNodeUuid,
): string {
  const segment = findSceneSegment(sceneUuid, nodeUuid)
  if (segment) {
    return segment.id
  }
  const linkNode = getIvSceneLinkNode(sceneUuid)
  if (linkNode) {
    return linkNode.uuid
  }
  return nodeUuid
}
